import { useState } from 'react';
import styled from 'styled-components';

import { AdvertiserSelector } from '@/components/common/AdvertiserSelector';
import { CampaignPlanSelector } from '@/components/common/CampaignPlanSelector';
import { InsertionOrderSelector } from '@/components/common/InsertionOrderSelector';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';
import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';

type Props = {
  onSelectionChange: (campaignPlanId?: string, insertionOrderId?: string) => void;
};

type State = {
  advertiser?: MediaAdvertiserUserInterface;
  campaignPlan?: CampaignPlan;
};

type SelectorEntityKey = 'advertiser' | 'campaignPlan' | 'insertionOrder';

const SelectorsGrid = styled.div`
  display: grid;
  grid-row-gap: 8px;
  grid-column-gap: 16px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(2, 1fr);
`;

export function EntitySelectorStep({ onSelectionChange }: Props) {
  const [{ advertiser, campaignPlan }, setState] = useState<State>({});

  const handleChange = (
    entityType: SelectorEntityKey,
    entitySelected: MediaAdvertiserUserInterface | CampaignPlan | InsertionOrder
  ) => {
    setState(prevState => {
      const newState: State = {
        ...prevState,
        ...(entityType === 'advertiser' && {
          advertiser: entitySelected as MediaAdvertiserUserInterface,
          campaignPlan: undefined
        }),
        ...(entityType === 'campaignPlan' && {
          campaignPlan: entitySelected as CampaignPlan
        })
      };

      if (entityType === 'advertiser') {
        onSelectionChange(undefined, undefined);
      } else if (entityType === 'campaignPlan') {
        onSelectionChange((entitySelected as CampaignPlan)._id, undefined);
      } else if (entityType === 'insertionOrder') {
        onSelectionChange(newState.campaignPlan?._id, (entitySelected as InsertionOrder)._id);
      }

      return newState;
    });
  };

  return (
    <SelectorsGrid>
      <div>
        <AdvertiserSelector id="lineItemWizardAdvertiserSelector" onChange={adv => handleChange('advertiser', adv)} />
      </div>
      {advertiser && (
        <div>
          <CampaignPlanSelector
            advertiserId={advertiser._id}
            id="lineItemWizardCampaignPlanSelector"
            key={advertiser._id}
            onChange={cp => handleChange('campaignPlan', cp)}
          />
        </div>
      )}
      {campaignPlan && (
        <div>
          <InsertionOrderSelector
            cpId={campaignPlan._id}
            id="lineItemWizardIOSelector"
            key={campaignPlan._id}
            onChange={io => handleChange('insertionOrder', io)}
          />
        </div>
      )}
    </SelectorsGrid>
  );
}
